import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaProjectDiagram, FaChartLine, FaCalculator, FaClipboardCheck, FaSearchDollar, FaFileContract } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled from "styled-components";

// Styles simplifiés
const FAQContainer = styled.div`
  width: 100%;
  background: white;
  padding: 30px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
`;

const FAQContent = styled.div`
  width: 100%;
`;

const FAQItem = styled.div`
  background: #ffffff;
  margin: 15px 0;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  
  &:hover {
    box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
  }
`;

const Question = styled.div`
  background: #f9fafb;
  padding: 18px;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
  border-radius: 8px;

  &:hover {
    background: #eef1f6;
    color: #E60000;
  }
`;

const Answer = styled.div`
  padding: 20px;
  font-size: 16px;
  line-height: 1.6;
  color: #555;
  background: #ffffff;
  border-top: 1px solid #eaeaea;
`;

const Icon = styled.span`
  font-size: 22px;
  font-weight: bold;
  color: #E60000;
  transition: transform 0.3s ease;
  ${({ isOpen }) => isOpen && "transform: rotate(180deg);"}
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 10px;
  padding: 30px;
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  height: 100%;
  margin-bottom: 20px;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const StepCard = styled.div`
  background: white;
  border-radius: 10px;
  padding: 30px;
  margin-bottom: 20px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  padding-left: 60px;
`;

const StepNumber = styled.div`
  position: absolute;
  top: 50%;
  left: 15px;
  transform: translateY(-50%);
  background: #FFD700;
  color: #333;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
`;

const CallToAction = styled.div`
  background: #E60000;
  padding: 40px 0;
  color: white;
  text-align: center;
  border-radius: 10px;
  margin: 60px auto;
  max-width: 700px;
`;

const CTAButton = styled.button`
  background: white;
  color: #E60000;
  border: none;
  padding: 12px 30px;
  font-size: 18px;
  font-weight: 600;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f8f8f8;
    transform: scale(1.05);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const FeatureIcon = styled.div`
  font-size: 40px;
  margin-bottom: 15px;
  color: ${props => props.color || "#E60000"};
`;

const ProjectDevelopment = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const faqData = [
    {
      question: "Pourquoi faire appel à un expert-comptable pour monter mon projet et mon business plan ?",
      answer: "L'expert-comptable apporte une expertise financière et stratégique indispensable pour structurer votre projet, valider sa viabilité, et convaincre les partenaires financiers. Il assure la fiabilité des prévisions et vous guide dans les choix clés."
    },
    {
      question: "À qui s'adresse ce service ?",
      answer: "Ce service s'adresse aux porteurs de projets, aux créateurs d'entreprise, aux entrepreneurs souhaitant développer une nouvelle activité, ou à ceux qui ont besoin de convaincre des investisseurs."
    },
    {
      question: "Qu'est-ce qu'un business plan et pourquoi est-il essentiel ?",
      answer: "Le business plan est un document qui présente votre projet, sa stratégie, ses prévisions financières, et ses besoins de financement. Il est essentiel pour convaincre les banques, les investisseurs, et pour vous guider dans la mise en œuvre de votre projet."
    },
    {
      question: "Quels services proposez-vous pour le montage de projets ?",
      answer: "Nous proposons : l'étude de faisabilité du projet, l'élaboration du business plan (analyse de marché, stratégie, prévisions financières), l'aide à la recherche de financements (prêts, subventions, investisseurs), le conseil en matière de choix juridiques et fiscaux, l'accompagnement dans les démarches administratives et la recherche de financements."
    },
    {
      question: "Pouvez-vous m'aider à réaliser mon prévisionnel financier ?",
      answer: "Oui, nous élaborons des prévisions financières réalistes et fiables, en tenant compte des spécificités de votre projet et de votre secteur d'activité."
    },
    {
      question: "Comment m'aidez-vous à trouver des financements ?",
      answer: "Nous vous aidons à identifier les sources de financement adaptées à votre projet (prêts bancaires, aides publiques, capital-risque), à préparer les dossiers de demande de financement, et à présenter votre projet aux investisseurs."
    }
  ];

  const projectSteps = [
    {
      title: "Diagnostic et Analyse Préliminaire",
      description: "Échange approfondi sur votre projet, analyse de votre marché et de votre concurrence, identification des forces, faiblesses, opportunités et menaces (SWOT)."
    },
    {
      title: "Étude de Marché Approfondie",
      description: "Analyse quantitative et qualitative de la demande, segmentation de la clientèle cible, étude des tendances du marché."
    },
    {
      title: "Définition du Modèle Économique",
      description: "Description de votre offre de produits ou services, définition de votre stratégie de prix et de distribution, identification de vos sources de revenus."
    },
    {
      title: "Stratégie de Développement",
      description: "Définition de vos objectifs à court, moyen et long terme, élaboration de votre plan d'actions marketing et commercial, choix des indicateurs clés de performance (KPI)."
    },
    {
      title: "Prévisions Financières Détaillées",
      description: "Compte de résultat prévisionnel sur 3 à 5 ans, plan de financement initial et à terme, plan de trésorerie, calcul du seuil de rentabilité, analyse des ratios financiers clés."
    },
    {
      title: "Rédaction du Business Plan",
      description: "Synthèse executive, présentation de l'équipe, description du projet, analyse du marché, stratégie marketing et commerciale, prévisions financières, annexes."
    },
    {
      title: "Accompagnement Post-Création (optionnel)",
      description: "Suivi des indicateurs, ajustements du Business Plan, conseils en gestion."
    }
  ];

  const serviceFeatures = [
    {
      icon: <FaProjectDiagram />,
      title: "Conception Stratégique",
      description: "Nous vous aidons à définir votre vision stratégique et à la transformer en objectifs opérationnels."
    },
    {
      icon: <FaChartLine />,
      title: "Analyse de Marché",
      description: "Étude approfondie de votre secteur, de la concurrence et de votre positionnement optimal."
    },
    {
      icon: <FaCalculator />,
      title: "Projections Financières",
      description: "Élaboration de prévisions financières réalistes et solides pour évaluer la viabilité de votre projet."
    },
    {
      icon: <FaClipboardCheck />,
      title: "Validation de Projet",
      description: "Analyse critique de la faisabilité pour vous aider à valider ou ajuster votre concept initial."
    },
    {
      icon: <FaSearchDollar />,
      title: "Recherche de Financements",
      description: "Identification des sources de financement adaptées à votre projet et aide à la préparation des dossiers."
    },
    {
      icon: <FaFileContract />,
      title: "Rédaction Professionnelle",
      description: "Mise en forme claire et percutante de votre business plan pour convaincre vos partenaires."
    }
  ];

  const benefits = [
    "Valider la faisabilité de votre projet.",
    "Définir une stratégie claire et cohérente.",
    "Convaincre vos partenaires financiers.",
    "Piloter votre activité et mesurer vos performances."
  ];

  return (
    <>
      <Header />

      {/* Banner Section */}
      <section className="service-banner d-flex align-items-center">
        <div className="container text-white text-start">
          <h1 className="fw-bold mb-4">Montage de Projets et Business Plan</h1>
          <p className="lead mb-4">Votre Feuille de Route pour le Succès Entrepreneurial</p>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/" className="text-white text-decoration-none">
                  Accueil
                </Link>{" "}
                /{" "}
                <Link to="/services" className="text-white text-decoration-none">
                  Services
                </Link>
              </li>
              <li className="breadcrumb-item active text-white" aria-current="page">
                Montage de Projets
              </li>
            </ol>
          </nav>
        </div>
      </section>

      {/* Intro Section */}
      <section className="service-intro py-5">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h2 className="fw-bold mb-4">Transformez Votre Vision en Projet Stratégique</h2>
              <p className="lead text-muted">
                Le business plan est bien plus qu'un simple document : c'est un outil de pilotage stratégique, un support de communication indispensable et un levier pour obtenir des financements.
              </p>
              <p className="text-muted">
                CINEF-EC vous accompagne dans la construction d'un business plan solide et sur-mesure, qui vous permettra de transformer vos idées en plans d'action concrets et réalisables. Notre expertise vous aide à développer un business plan qui met en valeur la viabilité et le potentiel unique de votre projet.
              </p>

              <div className="mt-4">
                <h5 className="fw-bold">Nos Business Plans vous permettent de :</h5>
                <ul className="list-group list-group-flush mt-3">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="list-group-item bg-transparent ps-0">
                      <div className="d-flex align-items-center">
                        <span className="me-2" style={{ color: "#228B22" }}>✓</span>
                        <span>{benefit}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <CTAButton className="mt-4">Demander un devis</CTAButton>
            </div>
          </div>
        </div>
      </section>

      {/* Services Features */}
      <section className="features py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">Nos Services de Montage de Projets</h2>
            <p className="text-muted">Une approche complète pour transformer votre idée en projet viable</p>
          </div>

          <div className="row">
            {serviceFeatures.map((feature, index) => (
              <div className="col-md-4 mb-4" key={index}>
                <ServiceCard>
                  <FeatureIcon color={index % 2 === 0 ? "#228B22" : "#E60000"}>
                    {feature.icon}
                  </FeatureIcon>
                  <h4 className="fw-bold mb-3">{feature.title}</h4>
                  <p className="text-muted">{feature.description}</p>
                </ServiceCard>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Steps */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">Nos Étapes Clés</h2>
            <p className="text-muted">Notre méthodologie éprouvée pour construire votre Business Plan</p>
          </div>

          <div className="row">
            {projectSteps.map((step, index) => (
              <div className="col-12" key={index}>
                <StepCard>
                  <StepNumber>{index + 1}</StepNumber>
                  <h4 className="fw-bold mb-3">{step.title}</h4>
                  <p className="text-muted mb-0">{step.description}</p>
                </StepCard>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <CallToAction>
        <div className="container">
          <h2 className="fw-bold mb-4">Prêt à concrétiser votre projet ?</h2>
          <p className="lead mb-4">Contactez-nous pour une étude gratuite et personnalisée de votre projet.</p>

          <Link to="/contact">
            <CTAButton>Prendre rendez-vous</CTAButton>
          </Link>
        </div>
      </CallToAction>

      {/* FAQ Section */}
      <section id="faq-section" className="container-fluid py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">Questions Fréquentes</h2>
            <p className="text-muted">Tout ce que vous devez savoir sur le montage de projets et business plan</p>
          </div>

          <FAQContainer>
            <FAQContent>
              {faqData.map((faq, index) => (
                <FAQItem key={index}>
                  <Question onClick={() => toggleFAQ(index)}>
                    {faq.question}
                    <Icon isOpen={activeIndex === index}>{activeIndex === index ? "−" : "+"}</Icon>
                  </Question>
                  {activeIndex === index && <Answer>{faq.answer}</Answer>}
                </FAQItem>
              ))}
            </FAQContent>
          </FAQContainer>
        </div>
      </section>

      <Footer />

      <style>{`
        .service-banner {
          background: linear-gradient(to right, rgba(0, 138, 68, 0.85), rgba(220, 53, 69, 0.85)), url("/assets/images/service-banner.jpg");
          background-size: cover;
          background-position: center;
          height: 400px;
          display: flex;
          align-items: center;
        }
        
        .advantage-icon {
          font-size: 2.5rem;
          margin-bottom: 1rem;
        }
        
        .lead {
          font-size: 1.25rem;
          font-weight: 300;
          line-height: 1.5;
        }
      `}</style>
    </>
  );
};

export default ProjectDevelopment;